import { commonActions } from '../../global-store/subjects/common';
import { getBooleanFromString } from '../../utils/string-utils';
import { skyFoundationSettings } from '../../services/configs/configs-service';
import {
  isEntertainmentDomain,
  isHomePage,
  isPremiumPageV2,
  isSectionHome,
  isStickyCarouselTemplateEnable,
  isSubSectionHome,
} from '../../utils/aem-utils';
import { isMobile, isSafari, isWidthDeviceMobileTablet } from '../../utils/device-utils';
import { isHomepageInArticle } from '../../utils/injected-hp-utils';
import { removeElementDom } from '../../utils/dom-utils';
import { initTvCardRail } from '../templating/tv-card-rail';
import { init as initStickyPlayer, stickyHeroPlayerDisclaimer } from '../sticky-hero-player/sticky-hero-player';
import { getAllCarouselData } from '../multiple-video-playlist/helpers';
import { isEmptyObject } from '../../utils/object-utils';

import {
  $heroPlaylist,
  CAROUSEL_ELEMENT_HOOK,
  HERO_PLAYER_CONTAINER,
  HERO_PLAYLIST_DELAY,
  PLAYER_CLASSNAME,
  PLAYER_TV_CARD_RAIL_JS_CLASS,
  PLAYER_WITH_CAROUSEL_ID_JS_CLASS,
  SAFARI_PLAYER_CARD_CLASS,
  VIDEO_ADVERTISING_CUSTOM_PARAM,
  VIDEO_PLAYER_CONTAINER_ID_PREFIX,
  VIDEO_PLAYER_PLAYERTYPE,
  VIDEO_PLAYER_SETUP_DEFAULTS,
  VIDEO_PLAYER_STATUS,
} from './constants';
import { playersState } from './state';

const $players = document.querySelectorAll(`${PLAYER_CLASSNAME}`);

export const isAudioOnArticleHeroPlayer = getBooleanFromString(skyFoundationSettings.getAudioOnArticleHeroPlayer());

export const isHeroPlayerType = (playerType) => playerType === VIDEO_PLAYER_PLAYERTYPE.HERO;
export const isCarouselPlayerType = (playerType) => playerType.includes(VIDEO_PLAYER_PLAYERTYPE.CAROUSEL);
export const isRailPlayerType = (playerType) => playerType === VIDEO_PLAYER_PLAYERTYPE.RAIL;

const mapPlayerCarousel = (player) => {
  player.wasInitialised = player.id !== PLAYER_WITH_CAROUSEL_ID_JS_CLASS ? true : null;
};

// GETTERS
const getPlayerInPageList = ($playersInPage) => {
  const playerList = ($playersInPage ? [...$playersInPage] : [...$players])
    .filter((item) => item.wasInitialised !== true)
    .filter((item) => item.dataset.videoid >= 1);
  playerList.forEach(mapPlayerCarousel);
  return playerList;
};

export const getDatasetValue = (dataset, defaultValue) => {
  return typeof dataset === 'string' && (dataset === 'true' || dataset === 'false')
    ? getBooleanFromString(dataset)
    : dataset || defaultValue;
};

const getPreviewImg = (previewimg) => {
  if (!previewimg) {
    return true;
  }
  if (previewimg === 'false') {
    return false;
  }
  return previewimg;
};

const getPlayerMute = (dataset) => {
  const isVolumeOverride = getDatasetValue(dataset.volumeOverride, VIDEO_PLAYER_SETUP_DEFAULTS.VOLUME_OVERRIDE);
  const isAutostart = getDatasetValue(dataset.autostart, VIDEO_PLAYER_SETUP_DEFAULTS.AUTOSTART);
  if (isAudioOnArticleHeroPlayer && isVolumeOverride) {
    return getDatasetValue(dataset.mute, VIDEO_PLAYER_SETUP_DEFAULTS.MUTE);
  } else if (!isAutostart) {
    return getDatasetValue(dataset.mute, false);
  }
  return true;
};

const getDaiOff = (isLive) => {
  return isLive ? getBooleanFromString(skyFoundationSettings.getDaiOff()) : VIDEO_PLAYER_SETUP_DEFAULTS.DAI_OFF;
};

const getIasTracker = (playerType, isLive) => {
  const $heroPlayerContainer = document.querySelector(HERO_PLAYER_CONTAINER);

  const isHeroTypeAndNotLive = isHeroPlayerType(playerType) && !isLive;
  const isCarouselTypeAndHeroLive =
    isCarouselPlayerType(playerType) && ((isHeroPlayerType(playerType) && isLive) || !$heroPlayerContainer);

  return Boolean(isHeroTypeAndNotLive || isCarouselTypeAndHeroLive);
};

// SETTERS

const getPlayerType = (playerData) => {
  const playerType = playerData.dataset.playertype;
  if (playerType === VIDEO_PLAYER_PLAYERTYPE.BODY) {
    playersState.setState('playerInBodyNumber', playersState.getState('playerInBodyNumber') + 1);
    return `${playerType}-${playersState.getState('playerInBodyNumber')}`;
  }
  if (isRailPlayerType(playerType)) {
    return VIDEO_PLAYER_PLAYERTYPE.STICKY;
  }
  if (isCarouselPlayerType(playerType)) {
    const stickyCarouselElement =
      document.querySelector(CAROUSEL_ELEMENT_HOOK)?.dataset?.stickycarouselactive === 'true';
    const stickyCarouselTemplateTrack = isStickyCarouselTemplateEnable();
    if (stickyCarouselTemplateTrack && stickyCarouselElement) {
      return playerType.concat(VIDEO_PLAYER_PLAYERTYPE.STICKY_TRACK);
    }
  }

  if (
    (isHomePage() || isSectionHome() || isSubSectionHome()) &&
    !getDatasetValue(playerData.dataset.autostart, VIDEO_PLAYER_SETUP_DEFAULTS.AUTOSTART) &&
    playerData.dataset.playertype === VIDEO_PLAYER_PLAYERTYPE.HERO
  ) {
    return VIDEO_PLAYER_PLAYERTYPE.HERO_HP;
  }

  return playerType;
};

/**
 * set playerList to common store
 * @playerConfigList return of playerConfBuilder
 * @playerList initialized player list
 */
export const setPlayerListToStore = ({ playerConfigList, playerList }) => {
  const playerListToStore = playerConfigList.map((playerConfig, index) => {
    return { ...playerList[index], playerConfig };
  });
  commonActions.setPlayersOnPage(playerListToStore);
};

/**
 * set volume override for player start
 * @playerConfigList return of playerConfBuilder
 * @playerList initialized player list
 */
export const setVolumeOverride = ({ playerConfigList, playerList }) => {
  playerConfigList.forEach((playerConfig, index) => {
    const $currentPlayer = document.getElementById(playerConfig.containerid);
    const isVolumeOverride = getDatasetValue(
      $currentPlayer?.dataset?.volumeOverride,
      VIDEO_PLAYER_SETUP_DEFAULTS.VOLUME_OVERRIDE
    );
    if (isVolumeOverride && !isMobile() && isAudioOnArticleHeroPlayer) {
      playerList[index].on(VIDEO_PLAYER_STATUS.READY, async () => {
        if ($heroPlaylist) {
          playerList[index].setVolume(playersState.getState('previousPlayerVolume'));
        } else {
          playerList[index].setVolume(1);
        }
      });
    }
  });
};

/**
 * Init tv card player
 * @param {*} dataset value of islive
 */

const initRailPlayer = (isLive) => {
  const $tvCard = document.querySelector(`${PLAYER_TV_CARD_RAIL_JS_CLASS}`);
  console.info(`[TV - CARD] init TV card RAIL isLive`, $tvCard);
  if (!$tvCard) {
    return;
  }
  if (isMobile() || isWidthDeviceMobileTablet()) {
    initTvCardRail({
      $tvCard,
      isLive,
      isLiveDesktop: false,
    });
  }
  if (!isMobile()) {
    if (!isLive) {
      initTvCardRail({
        $tvCard,
        isLiveDesktop: true,
      });
    }
  }
};

/**
 * Build player Setup object depending on playerData
 * @playerData playerData dataset of player
 * @playerType playerType type of player
 * @isLive dataset value of islive
 */
const buildPlayerSetup = ({ playerData, playerType, isLive }) => {
  const dataset = playerData?.dataset;
  const playerSetup = {
    id: getDatasetValue(dataset.videoid, VIDEO_PLAYER_SETUP_DEFAULTS.ID),
    live: isLive,
    advOff: getDatasetValue(dataset.advoff, VIDEO_PLAYER_SETUP_DEFAULTS.ADV_OFF),
    domain: VIDEO_PLAYER_SETUP_DEFAULTS.DOMAIN,
    autostart: getDatasetValue(dataset.autostart, VIDEO_PLAYER_SETUP_DEFAULTS.AUTOSTART),
    trackdomain: window.location.hostname || VIDEO_PLAYER_SETUP_DEFAULTS.TRACK_DOMAIN,
    playertype: playerType,
    preview: getPreviewImg(dataset.previewimg),
    ias: getIasTracker(playerType, isLive),
    nextVideo: getDatasetValue(dataset.nextVideo, VIDEO_PLAYER_SETUP_DEFAULTS.NEXT_VIDEO),
    countdown: getDatasetValue(dataset.countdown, VIDEO_PLAYER_SETUP_DEFAULTS.COUNTDOWN),
    daiOff: getDaiOff(isLive),
    social: getDatasetValue(dataset.social, VIDEO_PLAYER_SETUP_DEFAULTS.SOCIAL),
    mute: getPlayerMute(dataset),
  };

  if (isPremiumPageV2()) {
    playerSetup.advertising = {
      customParams: {
        premium: VIDEO_ADVERTISING_CUSTOM_PARAM,
      },
    };
  }

  return playerSetup;
};

/**
 * Build player Configuration depending on playerData
 * @param {*} playerData dataset of player
 */
const playerConfBuilder = (playerData) => {
  const containerid = playerData.id;
  const playerType = getPlayerType(playerData);
  const isLive = getDatasetValue(playerData.dataset.islive, false);

  if (isRailPlayerType(playerData.dataset.playertype)) {
    initRailPlayer(isLive);
  }

  const playersetup = buildPlayerSetup({
    playerData,
    playerType,
    isLive,
  });

  console.info(
    ` [ INIT - Player ] return player config containerId:${containerid} playerSetup:${JSON.stringify(playersetup)}`
  );
  return {
    containerid,
    playersetup,
  };
};

export const clearPlayersLive = () => {
  if (!isMobile() || isHomepageInArticle()) {
    const livePlayersNodeList = Array.from(document.querySelectorAll('div[data-islive="true"]'));
    const isLivePlayerInHero = livePlayersNodeList.filter(
      (element) => element.dataset.playertype === VIDEO_PLAYER_PLAYERTYPE.HERO
    );
    if (isLivePlayerInHero.length > 0) {
      // is live in hero, delete tv card box not in Entertainment.
      if (!isEntertainmentDomain()) {
        removeElementDom(document.querySelector(`${PLAYER_TV_CARD_RAIL_JS_CLASS}`));
        livePlayersNodeList.forEach((livePlayer) => {
          if (livePlayer.dataset.playertype !== VIDEO_PLAYER_PLAYERTYPE.HERO) {
            removeElementDom(livePlayer);
          }
        });
      }
    }
  }
};

export const addSafariPlayerCardClass = () => {
  if (isSafari()) {
    $players.forEach((player) => {
      player.classList.add(SAFARI_PLAYER_CARD_CLASS);
    });
  }
};

/**
 * Get Player Config List and check if the container exists in the DOM
 */
export const getPlayerConfigList = ($players) => {
  return [...getPlayerInPageList($players)]
    .map(playerConfBuilder)
    .filter((playerConf) => Boolean(playerConf) && Boolean(document.getElementById(playerConf.containerid)));
};

/**
 * Check Video Carousel Player status and position
 * @param {*} videoPlayer player instance
 */
export const initVideoCarouselListeners = (videoPlayer) => {
  Object.keys(VIDEO_PLAYER_STATUS)
    .filter((statusKey) => VIDEO_PLAYER_STATUS[statusKey] !== VIDEO_PLAYER_STATUS.INITIAL)
    .forEach((statusKey) => {
      videoPlayer.on(VIDEO_PLAYER_STATUS[statusKey], () => {
        commonActions.setVideoCarouselStatus(VIDEO_PLAYER_STATUS[statusKey]);
      });
    });
};

const getPlaylistType = () => {
  const parseJsonData = (data, type) => {
    const parsedData = JSON.parse(data);
    if (isEmptyObject(parsedData)) {
      return;
    }
    return {
      tabType: type,
      ...parsedData,
      [`${type}Type`]: true,
    };
  };

  if ($heroPlaylist?.dataset?.jsonTags) {
    return parseJsonData($heroPlaylist.dataset.jsonTags, 'tags');
  }
  if ($heroPlaylist?.dataset?.jsonSections) {
    return parseJsonData($heroPlaylist.dataset.jsonSections, 'sections');
  }
  if ($heroPlaylist?.dataset?.jsonPlaylist) {
    return parseJsonData($heroPlaylist.dataset.jsonPlaylist, 'playlist');
  }

  return undefined;
};

const getPlaylistData = async () => {
  const playlistType = [getPlaylistType()];
  if (!playlistType?.length) {
    return;
  }
  let successCarouselData = await getAllCarouselData({ initialTabsData: playlistType });
  if (!successCarouselData.length) {
    // Fallback to hlPrevTags
    successCarouselData = await getAllCarouselData({ initialTabsData: playlistType, hlPrevTags: true });
  }
  if (successCarouselData) {
    const mappedVideoIdValues = successCarouselData?.flatMap((obj) =>
      obj?.carouselData?.map((item) => {
        return {
          assetId: item?.assetId.toString(),
          assetTitle: item?.title,
        };
      })
    );
    if (mappedVideoIdValues?.length > 0) {
      playersState.setState('videosArray', mappedVideoIdValues);
    }
  }
};

// Init hero playlist
export const initHeroPlaylist = async ({ playerList, playerConfigList, skyPlayersManager, $playerInIntro }) => {
  playerConfigList.forEach((playerConfig, index) => {
    if (playerConfig.containerid === $playerInIntro.id) {
      playerList[index].on(VIDEO_PLAYER_STATUS.COMPLETE, async () => {
        if (!playersState.getState('videosArray')?.length) {
          await getPlaylistData();
        }
        const videosArray = playersState.getState('videosArray');
        playersState.setState('shouldReplayVideo', false);
        if (videosArray.length > playersState.getState('currentVideoIndex')) {
          setTimeout(() => {
            if (!playersState.getState('shouldReplayVideo')) {
              playersState.setState('previousPlayerVolume', playerList[index].getVolume());
              playerList[index].dispose();
              stickyHeroPlayerDisclaimer.dispose();
              $playerInIntro.dataset.id = `${VIDEO_PLAYER_CONTAINER_ID_PREFIX}${videosArray[playersState.getState('currentVideoIndex')]?.assetId}`;
              $playerInIntro.dataset.videoid = videosArray[playersState.getState('currentVideoIndex')]?.assetId;
              if ($playerInIntro.dataset.videotitle) {
                $playerInIntro.dataset.videotitle = videosArray[playersState.getState('currentVideoIndex')]?.assetTitle;
              }
              const { containerid, playersetup } = playerConfBuilder($playerInIntro);
              playerList[index] = skyPlayersManager.initPlayer(containerid, playersetup);
              playerConfigList[index] = { containerid, playersetup };
              initStickyPlayer({ playerList, playerConfigList });
              playersState.setState('currentVideoIndex', playersState.getState('currentVideoIndex') + 1);
            }
          }, HERO_PLAYLIST_DELAY);
        }
      });
      playerList[index].on(VIDEO_PLAYER_STATUS.REPLAY_CLICK, async () => {
        playersState.setState('shouldReplayVideo', true);
      });
    }
  });
};
